
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { WorkoutType } from "@/models/WorkoutType";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { DeleteCheck } from './types';

export const useWorkoutTypeQueries = () => {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();

  // Fetch workout types using React Query
  const {
    data: workoutTypes = [],
    isLoading: loading,
    error,
    refetch: fetchWorkoutTypes,
  } = useQuery({
    queryKey: ['workoutTypes', currentOrganization?.id],
    queryFn: async (): Promise<WorkoutType[]> => {
      if (!currentOrganization) {
        throw new Error('No organization selected');
      }

      const { data, error } = await supabase
        .from('workout_types')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching workout types:', error);
        toast.error(t('workoutTypes.errors.loadFailed'));
        throw error;
      }

      return data || [];
    },
    enabled: !!currentOrganization?.id,
  });

  const checkCanDeleteWorkoutType = async (id: string): Promise<DeleteCheck | null> => {
    try {
      const { data, error } = await supabase
        .rpc('can_delete_workout_type', { workout_type_id_param: id });

      if (error) {
        console.error('Error checking if workout type can be deleted:', error);
        throw error;
      }

      return data[0] as DeleteCheck;
    } catch (error) {
      console.error('Error checking if workout type can be deleted:', error);
      toast.error(t('workoutTypes.errors.deleteFailed'));
      return null;
    }
  };

  return {
    workoutTypes,
    loading,
    error,
    refetch: fetchWorkoutTypes,
    checkCanDeleteWorkoutType,
  };
};
